import React, { useEffect, useMemo, useState } from 'react'
import { Button, Select, TextField, TextArea } from '@ui'
import {
  deleteNotification,
  listAllNotifications,
  listOrgs,
  publishNotification,
} from '../api.js'

const TYPES = [
  { value: 'info',    label: 'Info',    bg: '#e1effe', fg: '#1e429f' },
  { value: 'warning', label: 'Warning', bg: '#fef3c7', fg: '#92400e' },
  { value: 'update',  label: 'Update',  bg: '#def7ec', fg: '#03543f' },
]

const ROLES = ['owner', 'admin', 'manager', 'report']

export function NotificationsPage() {
  const [orgs, setOrgs] = useState([])
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)

  const [form, setForm] = useState({
    targetOrg: 'GLOBAL',
    type: 'info',
    title: '',
    body: '',
    targetRoles: ['owner', 'admin', 'manager', 'report'],
    expiresAt: '',
  })

  const reload = () => {
    setLoading(true)
    listAllNotifications()
      .then((rows) => setItems(rows))
      .catch((e) => setErr(e?.message || String(e)))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    listOrgs().then(setOrgs).catch(() => {})
    reload()
  }, [])

  const toggleRole = (r) => {
    setForm((f) => ({
      ...f,
      targetRoles: f.targetRoles.includes(r)
        ? f.targetRoles.filter((x) => x !== r)
        : [...f.targetRoles, r],
    }))
  }

  const canSubmit = form.title.trim() && form.body.trim() && form.targetRoles.length > 0

  const submit = async (e) => {
    e.preventDefault()
    if (!canSubmit) return
    const input = { ...form }
    if (input.expiresAt) {
      // <input type="datetime-local"> yields "YYYY-MM-DDTHH:mm" with no timezone;
      // AppSync's AWSDateTime requires a full ISO8601 timestamp. Interpret the
      // value as local time and serialise with the browser's UTC offset.
      const d = new Date(input.expiresAt)
      if (isNaN(d.getTime())) delete input.expiresAt
      else input.expiresAt = d.toISOString()
    } else {
      delete input.expiresAt
    }
    try {
      await publishNotification(input)
      setForm((f) => ({ ...f, title: '', body: '' }))
      reload()
    } catch (err) {
      setErr(err?.message || String(err))
    }
  }

  const handleDelete = async (n) => {
    if (!confirm(`Delete "${n.title}"? This cannot be undone.`)) return
    try {
      await deleteNotification(n.id, n.targetOrg)
      reload()
    } catch (err) {
      setErr(err?.message || String(err))
    }
  }

  const typeMeta = useMemo(() => Object.fromEntries(TYPES.map((t) => [t.value, t])), [])

  return (
    <div>
      <h1>Notifications</h1>
      <p style={{ color: 'var(--text-secondary)' }}>
        Broadcast a banner to one org or every org. Users see the top message until
        they dismiss it; it stays browsable in the info section.
      </p>

      <form onSubmit={submit} style={styles.card}>
        <h2 style={styles.h2}>New notification</h2>
        {err ? <p style={styles.err}>{err}</p> : null}

        <div style={styles.row}>
          <Select
            label="Audience"
            value={form.targetOrg}
            onChange={(e) => setForm((f) => ({ ...f, targetOrg: e.target.value }))}
            style={{ flex: 1, minWidth: 160 }}
          >
            <option value="GLOBAL">All orgs</option>
            {orgs.map((t) => (
              <option key={t.slug} value={t.slug}>{t.name} ({t.slug})</option>
            ))}
          </Select>
          <Select
            label="Type"
            value={form.type}
            onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
            style={{ flex: 1, minWidth: 160 }}
          >
            {TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </Select>
          <TextField
            type="datetime-local"
            label="Expires (optional)"
            value={form.expiresAt}
            onChange={(e) => setForm((f) => ({ ...f, expiresAt: e.target.value }))}
            style={{ flex: 1, minWidth: 160 }}
          />
        </div>

        <TextField
          label="Title"
          maxLength={120}
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          required
        />

        <TextArea
          label="Body"
          rows={4}
          maxLength={4000}
          value={form.body}
          onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
          required
        />

        <div style={{ ...styles.row, alignItems: 'center' }}>
          <span style={{ ...styles.label, marginBottom: 0 }}>Roles</span>
          {ROLES.map((r) => (
            <label key={r} style={styles.checkbox}>
              <input
                type="checkbox"
                checked={form.targetRoles.includes(r)}
                onChange={() => toggleRole(r)}
              />
              {r}
            </label>
          ))}
        </div>

        <Button type="submit" disabled={!canSubmit}>
          Publish
        </Button>
      </form>

      <h2 style={styles.h2}>History</h2>
      {loading ? <p>Loading…</p> : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>When</th>
              <th style={styles.th}>Audience</th>
              <th style={styles.th}>Type</th>
              <th style={styles.th}>Title</th>
              <th style={styles.th}>Roles</th>
              <th style={styles.th}>Expires</th>
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((n) => {
              const meta = typeMeta[n.type] || typeMeta.info
              return (
                <tr key={n.id}>
                  <td style={styles.td}>{formatDate(n.createdAt)}</td>
                  <td style={styles.td}>{n.targetOrg === 'GLOBAL' ? 'All orgs' : n.targetOrg}</td>
                  <td style={styles.td}>
                    <span style={{ ...styles.pill, background: meta.bg, color: meta.fg }}>
                      {meta.label}
                    </span>
                  </td>
                  <td style={styles.td}>{n.title}</td>
                  <td style={styles.td}>{(n.targetRoles || []).join(', ')}</td>
                  <td style={styles.td}>{n.expiresAt ? formatDate(n.expiresAt) : '—'}</td>
                  <td style={styles.td}>
                    <Button variant="danger" size="sm" onClick={() => handleDelete(n)}>Delete</Button>
                  </td>
                </tr>
              )
            })}
            {items.length === 0 ? (
              <tr><td colSpan={7} style={{ ...styles.td, color: 'var(--text-secondary)' }}>No notifications yet.</td></tr>
            ) : null}
          </tbody>
        </table>
      )}
    </div>
  )
}

function formatDate(iso) {
  if (!iso) return ''
  try { return new Date(iso).toLocaleString() } catch { return iso }
}

const styles = {
  card: {
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '16px 20px',
    background: 'var(--card-bg, #fff)',
    marginBottom: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  h2: { fontSize: 14, fontWeight: 600, margin: '8px 0' },
  row: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  label: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, flex: 1, minWidth: 160 },
  checkbox: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 },
  err: { color: 'var(--danger-text)', fontSize: 12 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--text-secondary)' },
  td: { padding: '10px 12px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' },
  pill: { display: 'inline-block', fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 999 },
}
